import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OneOnOneRequestI } from 'screens/oneOnOneRabbit/interfaces/common';
import { z } from 'zod';

import { oneOneOneReactQueryKeys } from '../constants/oneOnOneReactQueryKeys';
import {
  getOneOnOneInstanceDetails,
  getOneOnOneInstanceDetailsFromMasterId,
  INVALID_DATE_ERROR,
  oneOnOneDetailsValidator,
  updateOneOnOneDetailsI,
  updateOneOnOneInstanceDetails,
} from '../services';

type OneOnOneInstanceDetails = z.infer<typeof oneOnOneDetailsValidator>;

export const useOneOnOneInstanceDetails = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();

  const data = queryClient.getQueryData<OneOnOneInstanceDetails>([
    oneOneOneReactQueryKeys.oneOnOneInstanceDetails,
    masterId,
    instanceId,
  ]);

  return data;
};

export const useOneOnOneInstanceDetailsQuery = ({ masterId, instanceId }: OneOnOneRequestI) =>
  useQuery(
    [oneOneOneReactQueryKeys.oneOnOneInstanceDetails, masterId, instanceId],
    () => getOneOnOneInstanceDetails({ masterId, instanceId }),
    {
      enabled: !Number.isNaN(masterId) && !Number.isNaN(instanceId),
    }
  );

export const useOneOnOneInstanceDetailsFromMasterIdQuery = ({ masterId }: OneOnOneRequestI) =>
  useQuery(
    [oneOneOneReactQueryKeys.oneOnOneInstanceDetails, masterId],
    () => getOneOnOneInstanceDetailsFromMasterId({ masterId }),
    {
      enabled: !Number.isNaN(masterId),
    }
  );

export const useUpdateInstanceDetailsMutation = ({
  masterId,
  instanceId,
  onError,
  onSuccess,
}: OneOnOneRequestI & {
  onError?: ({ err }: { err: any }) => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ updatedMeetingDetails }: { updatedMeetingDetails: updateOneOnOneDetailsI }) =>
      await updateOneOnOneInstanceDetails({
        masterId,
        instanceId,
        ...updatedMeetingDetails,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails, masterId, instanceId]);
      const previousInstanceDetails = queryClient.getQueryData([
        oneOneOneReactQueryKeys.oneOnOneInstanceDetails,
        masterId,
        instanceId,
      ]);

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneInstanceDetails, masterId, instanceId],
        (oldData: OneOnOneInstanceDetails) => ({
          ...oldData,
        })
      );

      return { previousInstanceDetails };
    },
    onError: (err, variables, context) => {
      if (context?.previousInstanceDetails) {
        queryClient.setQueryData(
          [oneOneOneReactQueryKeys.oneOnOneInstanceDetails, masterId, instanceId],
          context.previousInstanceDetails
        );
      }
      // @ts-ignore
      if (err?.name === INVALID_DATE_ERROR) {
      }
      onError && onError({ err });
    },
    onSuccess: () => {
      onSuccess && onSuccess();
    },
    onSettled: () => {
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneInstanceDetails, masterId, instanceId]);
    },
  });
};
